/**
 * THE OFFICIAL-IMPORTER TICK — one mark, everywhere a flagged brand appears.
 *
 * The owner, twice: "the location of official importer isn't located good",
 * and "the V of official importer more clear and shown here and everywhere".
 * Two complaints, and the second one is why this file exists at all.
 *
 * ── "EVERYWHERE" MEANS ONE COMPONENT, NOT FOUR RESEMBLANCES ──────────────
 *
 * The flag was drawn three different ways: a pill with a word in it under the
 * brand's name on the strip (truncated to "fficial importer" at 414 — measured,
 * not guessed), a 22px scrim disc on the brand card's cover, and nothing at all
 * on the brand page's own heading. Four surfaces cannot be made consistent by
 * four stylesheets agreeing to look alike; there is one component and one
 * geometry, and the only thing that varies is the GROUND it is dropped onto.
 *
 * ── "MORE CLEAR" IS A RATIO, AND THIS ONE IS THE PAGE'S OWN ──────────────
 *
 * On the page the disc is `--shop-text` and the glyph is `--shop-tint`: the
 * page's own solved foreground pair, used inverted. That is not "a contrasty
 * choice" — it is exactly the ratio the page already guarantees for its body
 * copy (≥4.5:1 by `tintForeground`), so the tick is as legible as the text
 * beside it on every tint, in all three background modes, by construction
 * rather than by inspection.
 *
 * On a brand's COVER PHOTOGRAPH it cannot be, for backlog item 20's reason: a
 * page-derived disc on an uploaded photo is a colour with no relationship to
 * anything behind it. There it takes the named photo exception —
 * `--shop-photo-scrim` under `--shop-photo-ink` — which is the same pair the
 * like button and the deal badge already use, and the class is still called
 * `photoTick` so the `oneColourSystem` consumer enumeration keeps naming it.
 *
 * ── "LOCATED GOOD" ───────────────────────────────────────────────────────
 *
 * Pinned to the mark's own top-inline-end corner, which is where a
 * verification mark has lived since Twitter put one there — not stacked under
 * the name in the reading flow, where it competed with the brand for the line
 * and lost. `inset-inline-end` and not `right`: this storefront runs Arabic
 * and Hebrew, and a physically-pinned badge lands on the wrong corner in both.
 *
 * The glyph is drawn at `strokeWidth` 3 rather than react-icons' default 2 —
 * a 20px tick at the default weight is a hairline, which is the whole of "more
 * clear" at this size.
 *
 * Class names checked against `modal-fixes.css`'s `[class*="panel"]` /
 * `[class*="overlay"]` / `[class*="popup"]` / `[class*="modal"]` wildcards.
 */
import React from 'react';
import { FiCheck } from 'react-icons/fi';
import { officialImporterNames, joinBrandNames } from '../../utils/productBrands';
import styles from './officialImporterTick.module.css';

/** A hairline tick is the complaint. One number, read by the guard. */
export const TICK_STROKE_WIDTH = 3;

/**
 * @param decorative  the claim is ALREADY IN WORDS beside this disc, so the
 *   disc is punctuation on it rather than a second statement of it. See
 *   `OfficialImporterNote` below for the measurement that forced the flag.
 */
export default function OfficialImporterTick({
  t, on = 'page', pinned = false, decorative = false, testId, className,
}) {
  const label = t('shop.officialImporter', { defaultValue: 'Official importer' });
  return (
    <span
      className={[
        styles.tick,
        on === 'photo' ? styles.photoTick : styles.pageTick,
        pinned ? styles.pinned : '',
        className,
      ].filter(Boolean).join(' ')}
      data-testid={testId}
      data-tick-ground={on}
      data-tick-pinned={pinned ? 'true' : undefined}
      title={label}
      /* Removed from the tree ENTIRELY, not just unlabelled: an empty span
       * with a `title` is still announced by some readers, which is the same
       * duplicate by another route. */
      aria-hidden={decorative || undefined}
    >
      <FiCheck aria-hidden strokeWidth={TICK_STROKE_WIDTH} />
      {/* The mark is not decoration — it is a claim about the business — so
        * where it stands ALONE it is in the accessibility tree as words rather
        * than as a shape. Where a sentence already makes the claim, those
        * words would be the claim twice; see `decorative`. */}
      {!decorative && <span className={styles.srOnly}>{label}</span>}
    </span>
  );
}

/**
 * "OFFICIAL IMPORTER OF X" — backlog item 27, and it NAMES the brands.
 *
 * Exported because it belongs on more than one surface: the shop's main page
 * (above the strip), the brand page of a brand that carries the flag, and the
 * collection page's brand block — the owner listed all three, and named the
 * collections page in particular ("it's important for them as it brings
 * trust"). One component so the sentence is written once and cannot be phrased
 * two ways. Renders nothing when there is nothing to name — a claim with an
 * empty list is worse than no claim.
 *
 * ── IT SAYS THE CLAIM ONCE ───────────────────────────────────────────────
 *
 * Measured on the running stack, all three surfaces, Hebrew and Arabic alike:
 * `note.textContent` came out "المستورد الرسميالمستورد الرسمي لـ DANIEL KLEIN"
 * — the tick's own sr-only label, then the sentence that contains the same
 * words. A reader heard "Official importer, official importer of Daniel
 * Klein". Inside a sentence that already makes the claim the disc is
 * PUNCTUATION, so it is `decorative` here and labelled everywhere it stands
 * alone. It is not removed: "the V of official importer more clear and shown
 * here and everywhere" is still the owner's instruction, and it is still drawn.
 *
 * @param showTick  false where the SAME brand's tick is already pinned to its
 *   artwork a few pixels above — the brand page. Two identical ticks measured
 *   143px (414) and 169px (1440) apart in one hero is not "shown everywhere",
 *   it is the mark meaning nothing.
 */
export function OfficialImporterNote({
  brands, language, t, showTick = true, testId = 'shop-official-importer', className,
}) {
  const names = officialImporterNames(brands, language);
  if (names.length === 0) return null;
  const joined = joinBrandNames(names, language, t('common.and', { defaultValue: 'and' }));
  return (
    <p className={[styles.note, className].filter(Boolean).join(' ')} data-testid={testId}>
      {/* THE SAME TICK, ON THE SENTENCE TOO — "shown here and everywhere". */}
      {showTick && (
        <OfficialImporterTick
          t={t}
          decorative
          testId={`${testId}-tick`}
          className={styles.noteTick}
        />
      )}
      <span>
        {t('shop.officialImporterOf', {
          defaultValue: 'Official importer of {{brands}}',
          brands: joined,
        })}
      </span>
    </p>
  );
}
